import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import DashboardChanges from './DashboardChanges';
import { Activity, ChevronRight, CircleCheck, TriangleAlert } from 'lucide-react';
import { Badge, Meter, Segmented } from '../design/ui';
import { formatShortTime } from '../utils/dates';
import './DashboardInsights.css';

// Reasons an open ticket waits for a human, most urgent first. Each ticket is
// counted under exactly one reason (backend ticket_work_state), so the
// reasons always add up to the headline and match the row flags of the
// ticket list. Clicking one filters the queue below through the backend
// `attention_reason` parameter — the same classifier that produced the count.
const ATTENTION_REASONS = [
  'reply_to_validate',
  'customer_unanswered',
  'carrier_contact_failed',
  'carrier_contact_to_validate',
  'flagged_action_required'
];

const NS = 'ticketListsUi';
const ACTIVITY_OPEN_KEY = 'una_dashboard_activity_open';

// Per-viewer convenience: whether « Activité » is unfolded. Storage may be
// unavailable (private window): the card then simply starts folded.
const readActivityOpen = () => {
  try {
    return window.localStorage.getItem(ACTIVITY_OPEN_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const writeActivityOpen = (open) => {
  try {
    window.localStorage.setItem(ACTIVITY_OPEN_KEY, open ? '1' : '0');
  } catch (error) {
    // nothing to do: the state just is not remembered
  }
};

// Daily AI share of resolutions over the last 14 days. Days without
// resolutions plot as 0.
const AutomationSparkline = ({ daily, title }) => {
  const points = (daily || []).slice(-14);
  if (points.length < 2) return null;
  const width = 160;
  const height = 28;
  const path = points
    .map((p, i) => {
      const share = p.resolved ? (p.resolved_ai || 0) / p.resolved : 0;
      const x = (i / (points.length - 1)) * width;
      const y = height - 2 - Math.min(share, 1) * (height - 4);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg className="dash-insights__spark" viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label={title}>
      <title>{title}</title>
      <path d={path} fill="none" stroke="var(--brand-accent)" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

// Why the agent asked a person for something (backend
// dashboard_insights_service.AUTONOMY_CAUSES, in its order).
const AUTONOMY_CAUSES = {
  handover: 'passé la main à une personne',
  carrier_validation: 'demande transporteur à valider',
  safety: 'réponse retenue par un contrôle',
  client_setting: 'validation demandée par le réglage du client',
  pilot: 'réveil en test',
  other: 'brouillon sans cause enregistrée',
};

const fmtFr = (value) => Number(value).toLocaleString('fr-FR').replace(/\u202f/g, '\u00a0');
const fmtDecimal = (value) => String(value).replace('.', ',');

// KI-242: the backend serves these numbers from a per-account cache (fresh
// 60 s, up to 15 min while a recompute runs). A stale answer is replaced once,
// silently, after the background recompute had time to finish.
const STALE_REFETCH_MS = 4000;

// « 14:05 » — the shared short format (utils/dates: parseUtc, Paris time).
const formatUpdatedAt = (generatedAt) => formatShortTime(generatedAt, undefined, { empty: null });

// Placeholder with the anatomy of the real block (attention strip + the
// activity card), shown while the first answer is on its way.
const InsightsSkeleton = () => (
  <div className="dash-insights dash-insights__skeleton" aria-hidden="true">
    <span className="u-skeleton dash-insights__skeleton-strip" />
    <div className="u-card dash-insights__skeleton-card">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="dash-insights__skeleton-metric">
          <span className="u-skeleton" style={{ width: '45%', height: 10 }} />
          <span className="u-skeleton" style={{ width: '60%', height: 20 }} />
        </span>
      ))}
    </div>
  </div>
);

const DashboardInsights = ({
  windowDays = 7,
  activeReason = null,
  onReasonSelect = null,
  onWindowChange = null,
  onLoaded = null,
  // « Activité » holds account figures: not for employees (Kevin 29/10 —
  // a team member sees their own productivity only).
  showActivity = true,
}) => {
  const { t } = useTranslation();
  const tl = (key, fallback, options) => t(`${NS}.${key}`, fallback, options);
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activityOpen, setActivityOpen] = useState(readActivityOpen);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    let isSubscribed = true;
    let refetchTimer = null;
    const fetchInsights = async (allowRefetch) => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        const response = await axios.get(`/api/tickets/dashboard-insights?window_days=${windowDays}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!isSubscribed) return;
        setInsights(response.data);
        if (onLoadedRef.current) onLoadedRef.current(response.data);
        const cacheState = response.data?.cache?.state || '';
        if (allowRefetch && cacheState.startsWith('stale')) {
          refetchTimer = setTimeout(() => fetchInsights(false), STALE_REFETCH_MS);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard insights:', error);
        if (isSubscribed && onLoadedRef.current) onLoadedRef.current(null);
      } finally {
        if (isSubscribed) setIsLoading(false);
      }
    };
    fetchInsights(true);
    return () => {
      isSubscribed = false;
      if (refetchTimer) clearTimeout(refetchTimer);
    };
  }, [windowDays]);

  if (isLoading) {
    return <InsightsSkeleton />;
  }
  if (!insights) return null;

  // Kevin 29/10: backlog, flow, blocked and satisfaction told the team
  // nothing; « Activité » keeps the automation share and what changed this
  // week. Where every open ticket stands is the pipeline above the list.
  const { kpis = {}, attention = {}, series = {}, thresholds = {} } = insights;
  const w = insights.window_days || windowDays;
  const oldAfterDays = thresholds.attention_old_after_days ?? 7;
  const attentionOld = attention[`older_than_${oldAfterDays}d`] || 0;
  const reasonsByKey = Object.fromEntries((attention.by_reason || []).map((item) => [item.reason, item]));
  const visibleReasons = ATTENTION_REASONS.filter((reason) => reasonsByKey[reason]?.count);

  // Resolutions the AI share is computed on (its scope line) — a one-time
  // backlog cleanup (the purge) is left out by the backend and named here.
  const resolved = kpis.resolved_7d?.current;
  const cleanupResolved = kpis.cleanup_resolved_7d || 0;
  const aiShare = kpis.ai_resolution_share_7d;
  const aiSharePrev = kpis.ai_resolution_share_prev_7d;
  const hasAiShare = aiShare !== null && aiShare !== undefined;
  const pointsDelta = (value, previous) => (
    value !== null && value !== undefined && previous !== null && previous !== undefined
      ? Math.round((value - previous) * 10) / 10
      : null
  );
  const aiDelta = pointsDelta(aiShare, aiSharePrev);
  // Kevin 05/11: the tickets the agent worked on without asking a person for
  // anything, against those where it asked (by cause).
  const autonomy = insights.autonomy || null;
  const hasAutonomy = Boolean(autonomy) && autonomy.share !== null && autonomy.share !== undefined;
  const autonomyDelta = hasAutonomy ? pointsDelta(autonomy.share, autonomy.share_prev) : null;
  // What looked like a request and is not (Kevin 05/11): drafts the agent
  // overtook by sending, carrier requests the case moved on without.
  const notCounted = (autonomy && autonomy.not_counted) || {};
  const deltaBadge = (delta) => (delta !== null && delta !== 0 ? (
    <Badge tone={delta > 0 ? 'success' : 'danger'}>
      {t('deltaPts', '{{pts}} pts', { pts: `${delta > 0 ? '+' : ''}${fmtDecimal(delta)}` })}
    </Badge>
  ) : null);

  // The chip lists the tickets of the snapshot whose counter was clicked
  // (backend attention_snapshot), not a newer classification.
  const handleReason = (reason) => {
    if (!onReasonSelect) return;
    if (activeReason === reason) {
      onReasonSelect(null, null);
    } else {
      onReasonSelect(reason, insights.snapshot_id || null);
    }
  };
  const updatedAt = formatUpdatedAt(insights.generated_at);
  const toggleActivity = () => {
    setActivityOpen((open) => {
      writeActivityOpen(!open);
      return !open;
    });
  };

  const periodLabel = tl('activity.period', '{{days}} j', { days: w });

  return (
    <div className="dash-insights">
      {attention.total > 0 ? (
        <section className="u-card dash-insights__attention" aria-labelledby="dash-attention-title">
          <div
            className="dash-insights__attention-head"
            title={t(
              'dashboardInsights.attentionHint',
              "Tickets ouverts dont la prochaine étape revient à votre équipe, chacun compté une seule fois sous sa raison la plus urgente. Les réponses envoyées hors d'Unanimity (par exemple directement depuis Outlook) ne sont pas visibles ici."
            )}
          >
            <span className="u-icon-chip u-icon-chip--warning" aria-hidden="true"><TriangleAlert strokeWidth={1.8} /></span>
            <div className="dash-insights__attention-text">
              <span className="u-overline">{tl('attention.overline', 'À traiter')}</span>
              <h2 className="dash-insights__attention-title" id="dash-attention-title">
                <span className="dash-insights__attention-total">{fmtFr(attention.total)}</span>
                <span>{t('dashboardInsights.attentionTitle', 'tickets attendent votre équipe')}</span>
              </h2>
              {attentionOld > 0 && (
                <span className="dash-insights__attention-old">
                  {t('dashboardInsights.attentionOld', 'dont {{count}} depuis plus de {{days}} jours', { count: attentionOld, days: oldAfterDays })}
                </span>
              )}
            </div>
          </div>
          <div className="dash-insights__reasons" role="group" aria-label={tl('attention.filterLabel', 'Afficher les tickets d’une raison')}>
            {visibleReasons.map((reason) => {
              const item = reasonsByKey[reason];
              const old = item[`older_than_${oldAfterDays}d`] || 0;
              const active = activeReason === reason;
              return (
                <button
                  key={reason}
                  type="button"
                  className={`dash-insights__reason${active ? ' is-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => handleReason(reason)}
                  title={`${t(`dashboardInsights.reasonHint_${reason}`, '')} ${t('dashboardInsights.reasonOld', '{{count}} depuis plus de {{days}} jours.', { count: old, days: oldAfterDays })}`.trim()}
                >
                  <b className="dash-insights__reason-count">{item.count}</b>
                  <span className="dash-insights__reason-label">{t(`dashboardInsights.flag_${reason}`, reason)}</span>
                  {old > 0 && (
                    <span className="dash-insights__reason-old">{tl('attention.old', '{{count}} > {{days}} j', { count: old, days: oldAfterDays })}</span>
                  )}
                  <ChevronRight className="dash-insights__reason-arrow" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </section>
      ) : (
        <div className="u-banner u-banner--success dash-insights__clear" role="status">
          <CircleCheck strokeWidth={2} aria-hidden="true" />
          <div className="u-banner__body">
            <span className="u-banner__title">{t('dashboardInsights.allClear', "Aucun ticket n'attend votre équipe")}</span>
          </div>
        </div>
      )}

      {showActivity && (
      <section className="u-card dash-insights__activity">
        <div className="dash-insights__activity-head">
          <button
            type="button"
            className="dash-insights__toggle"
            aria-expanded={activityOpen}
            aria-controls="dash-activity-metrics"
            onClick={toggleActivity}
          >
            <ChevronRight className={`dash-insights__toggle-icon${activityOpen ? ' is-open' : ''}`} aria-hidden="true" />
            <Activity className="dash-insights__toggle-mark" aria-hidden="true" />
            <span className="dash-insights__toggle-label">{tl('activity.title', 'Activité')}</span>
            <span className="dash-insights__toggle-period">· {periodLabel}</span>
          </button>
          {!activityOpen && (
            <div className="dash-insights__summary">
              {hasAutonomy && (
                <span><b>{fmtDecimal(autonomy.share)}&nbsp;%</b> {tl('activity.summaryAutonomy', 'traités sans aide par l’agent')}</span>
              )}
              {hasAiShare && (
                <span><b>{fmtDecimal(aiShare)}&nbsp;%</b> {tl('activity.summaryAi', 'résolus par l’IA')}</span>
              )}
            </div>
          )}
          <span className="u-toolbar__spacer" />
          {updatedAt && (
            <span
              className="dash-insights__freshness"
              title={t('dashboardInsights.freshnessHint', 'Chiffres calculés à partir de chaque ticket ouvert à cette heure-là, recalculés automatiquement pendant que le tableau de bord est utilisé.')}
            >
              {t('dashboardInsights.freshness', 'mis à jour à {{time}}', { time: updatedAt })}
            </span>
          )}
          {onWindowChange && (
            <Segmented
              options={[
                { value: 7, label: tl('activity.days7', '7 jours') },
                { value: 30, label: tl('activity.days30', '30 jours') },
              ]}
              value={windowDays}
              onChange={onWindowChange}
              label={tl('activity.periodLabel', 'Période des chiffres d’activité')}
            />
          )}
        </div>

        {activityOpen && (
          <div className="dash-insights__metrics dash-insights__metrics--changes" id="dash-activity-metrics">
            {hasAutonomy && (
              <div
                className="dash-insights__metric"
                title={tl('activity.autonomyHint', 'Tickets sur lesquels l’agent a répondu, préparé une réponse ou jugé qu’il n’y avait rien à envoyer pendant la période. Sans aide : il n’a rien demandé à une personne (ni passation, ni réponse à valider, ni demande transporteur à valider). Ne comptent pas : un brouillon que l’agent a lui-même remplacé en envoyant une réponse, une demande transporteur devenue sans objet (dossier passé en indemnisation, NPEC, retour ou clos).')}
              >
                <span className="u-overline">{tl('activity.autonomyTitle', 'Autonomie de l’agent')} · {periodLabel}</span>
                <div className="dash-insights__value">
                  <span className="dash-insights__number">{fmtDecimal(autonomy.share)}<span className="u-kpi__unit">%</span></span>
                  {deltaBadge(autonomyDelta)}
                </div>
                <span className="dash-insights__context">
                  {tl('activity.autonomyContext', '{{autonomous}} tickets sur {{handled}} traités sans rien demander à l’équipe', {
                    autonomous: fmtFr(autonomy.autonomous), handled: fmtFr(autonomy.handled),
                  })}
                </span>
                <Meter value={Math.min(100, autonomy.share)} max={100} label={tl('activity.autonomyTitle', 'Autonomie de l’agent')} />
                {(autonomy.by_cause || []).length > 0 && (
                  <div className="dash-insights__causes">
                    <span className="dash-insights__causes-title">
                      {tl('activity.autonomyAsked', 'Action demandée à l’équipe sur {{formatted}} tickets', {
                        count: autonomy.action_requested, formatted: fmtFr(autonomy.action_requested),
                      })}
                    </span>
                    <ul className="dash-insights__causes-list">
                      {autonomy.by_cause.map((item) => (
                        <li key={item.cause}>
                          <span>{tl(`activity.autonomyCause.${item.cause}`, AUTONOMY_CAUSES[item.cause] || item.cause)}</span>
                          <b>{fmtFr(item.count)}</b>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(notCounted.taken_back > 0 || notCounted.moot_carrier_request > 0) && (
                  <span className="dash-insights__causes-note">
                    {tl('activity.autonomyNotCounted', 'Non comptés : {{taken}} brouillons repris par l’agent · {{moot}} demandes transporteur sans objet', {
                      taken: fmtFr(notCounted.taken_back || 0), moot: fmtFr(notCounted.moot_carrier_request || 0),
                    })}
                  </span>
                )}
              </div>
            )}
            {hasAiShare && (
              <div className="dash-insights__metric">
                <span className="u-overline">{tl('activity.resolvedTitle', 'Résolus par l’agent IA')} · {periodLabel}</span>
                <div className="dash-insights__value">
                  <span className="dash-insights__number">{fmtDecimal(aiShare)}<span className="u-kpi__unit">%</span></span>
                  {deltaBadge(aiDelta)}
                </div>
                <span className="dash-insights__context">
                  {Number.isFinite(resolved)
                    ? t('dashboardInsights.automationScope', 'sur {{total}} tickets résolus', { total: fmtFr(resolved) })
                    : t('automationSub', "des résolutions faites par l'IA")}
                  {cleanupResolved > 0 && ` · ${tl('activity.cleanupExcluded', 'hors purge ({{formatted}} anciens tickets clôturés)', {
                    count: cleanupResolved, formatted: fmtFr(cleanupResolved),
                  })}`}
                </span>
                <Meter value={Math.min(100, aiShare)} max={100} label={tl('activity.resolvedTitle', 'Résolus par l’agent IA')} />
                <AutomationSparkline
                  daily={series.daily}
                  title={tl('activity.sparkline', 'Part des résolutions faites par l’IA, jour par jour sur 14 jours')}
                />
              </div>
            )}
            <div className="dash-insights__metric dash-insights__metric--changes">
              <span className="u-overline">{t('dashChangesUi.title', 'Cette semaine')}</span>
              <DashboardChanges breakdowns={insights.breakdowns} />
            </div>
          </div>
        )}
      </section>
      )}
    </div>
  );
};

export default DashboardInsights;
